import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    FormHelperText,
    FormLabel,
    Card,
    CardContent,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomFileUploads from "src/views/common/CustomFileUploads";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { MEDIA_URL } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import PageHeader from "src/@core/components/page-header";

const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: "#d0d7de",
        },
        "&:hover fieldset": {
            borderColor: "#7338AC",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#7338AC",
        },
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#7338AC",
    },
};

const validationSchema = yup.object().shape({
    title: yup.string().required("Title is required"),
    short_description: yup.string().required("Short description is required"),
    description: yup.string().required("Description is required"),
    region: yup.string().required("Region is required"),
    ai_setting: yup.object().shape({
        Genre: yup.string().required("Genre is required"),
        Scale: yup.string().required("Scale is required"),
        Tempo: yup.string().required("Tempo is required"),
    }),
    ai_prompt_title: yup.string(),
    ai_prompt_description: yup.string(),
    notation_title: yup.string(),
    notation_description: yup.string(),
    notation_files: yup.mixed(),
    audio_title: yup.string(),
    audio_description: yup.string(),
    audio_files: yup.mixed(),
    icon: yup.mixed(),
    // Removed sample_files from validation schema
});

const AddEditCulturalModule = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("");
    const [regions, setRegions] = useState([]);
    const [fetching, setFetching] = useState(false);
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: "",
            short_description: "",
            description: "",
            region: "",
            ai_setting: {
                Genre: "",
                Scale: "",
                Tempo: "",
            },
            ai_prompt_title: "",
            ai_prompt_description: "",
            notation_title: "",
            notation_description: "",
            notation_files: [],
            audio_title: "",
            audio_description: "",
            audio_files: [],
            icon: [],
        },
        resolver: yupResolver(validationSchema),
        mode: "onChange",
    });
    // Determine mode from pathname
    useEffect(() => {
        if (location.pathname.includes("/cultural-module/add")) {
            setMode("add");
        } else if (location.pathname.includes("/cultural-module/edit") && id) {
            setMode("edit");
        }
    }, [location.pathname, id]);

    // Fetch regions for dropdown
    useEffect(() => {
        fetchRegions();
    }, []);

    const fetchRegions = () => {
        axiosInstance
            .get(ApiEndPoints.REGIONS.list)
            .then((response) => {
                console.log("Regions:", response.data?.data?.regions);
                setRegions(response.data?.data?.regions || []);
            })
            .catch((error) => {
                console.error("Error fetching regions:", error);
            });
    };

    // Fetch data for edit mode
    useEffect(() => {
        if (mode === "edit" && id) {
            fetchCulturalModuleData();
        } else if (mode === "add") {
            reset({
                title: "",
                short_description: "",
                description: "",
                region: "",
                ai_setting: {
                    Genre: "",
                    Scale: "",
                    Tempo: "",
                },
                ai_prompt_title: "",
                ai_prompt_description: "",
                notation_title: "",
                notation_description: "",
                notation_files: [],
                audio_title: "",
                audio_description: "",
                audio_files: [],
                icon: [],
            });
        }
    }, [mode, id]);

    const fetchCulturalModuleData = () => {
        setFetching(true);
        axiosInstance
            .get(ApiEndPoints.CULTURAL.listById(id))
            .then((response) => {
                const data = response.data?.data?.module;
                console.log("fetchdata", data);
                console.log("notation_files from API:", data?.notation_files);
                console.log("audio_files from API:", data?.audio_files);
                if (data) {
                    reset({
                        title: data.title || "",
                        short_description: data.short_description || "",
                        description: data.description || "",
                        region: data.region || "",
                        ai_setting: {
                            Genre: data.ai_setting?.Genre || "",
                            Scale: data.ai_setting?.Scale || "",
                            Tempo: data.ai_setting?.Tempo || "",
                        },
                        ai_prompt_title: data.ai_prompt_title || "",
                        ai_prompt_description: data.ai_prompt_description || "",
                        notation_title: data.notation_title || "",
                        notation_description: data.notation_description || "",
                        notation_files: data.notation_files || [],
                        audio_title: data.audio_title || "",
                        audio_description: data.audio_description || "",
                        audio_files: data.audio_files || [],
                        icon: data.icon || null,
                    });
                }
            })
            .catch((error) => {
                console.error("Error fetching cultural module:", error);
                toastError("Failed to load cultural module data");
                navigate("/cultural-module");
            })
            .finally(() => {
                setFetching(false);
            });
    };

    const onSubmit = async (data) => {
        setLoading(true);
        const formData = new FormData();
        formData.append("title", data.title);
        formData.append("short_description", data.short_description);
        formData.append("description", data.description);
        formData.append("region", data.region);
        formData.append("ai_setting[Genre]", data.ai_setting.Genre);
        formData.append("ai_setting[Scale]", data.ai_setting.Scale);
        formData.append("ai_setting[Tempo]", data.ai_setting.Tempo);

        // Append text fields
        if (data.ai_prompt_title) formData.append("ai_prompt_title", data.ai_prompt_title);
        if (data.ai_prompt_description) formData.append("ai_prompt_description", data.ai_prompt_description);
        if (data.notation_title) formData.append("notation_title", data.notation_title);
        if (data.notation_description) formData.append("notation_description", data.notation_description);
        if (data.audio_title) formData.append("audio_title", data.audio_title);
        if (data.audio_description) formData.append("audio_description", data.audio_description);

        // Append notation_files as array (only send File objects, skip existing file objects)
        if (data.notation_files) {
            if (Array.isArray(data.notation_files)) {
                data.notation_files.forEach((file) => {
                    // Only append new File objects, skip existing files (objects with filePath)
                    if (file instanceof File) {
                        formData.append("notation_files", file);
                    }
                    // If it's a string path, append it
                    else if (typeof file === "string") {
                        formData.append("notation_files", file);
                    }
                    // Skip objects with filePath (existing files on server)
                });
            } else if (data.notation_files instanceof File) {
                formData.append("notation_files", data.notation_files);
            } else if (typeof data.notation_files === "string") {
                formData.append("notation_files", data.notation_files);
            }
        }

        // Append audio_files as array (only send File objects, skip existing file objects)
        if (data.audio_files) {
            if (Array.isArray(data.audio_files)) {
                data.audio_files.forEach((file) => {
                    // Only append new File objects, skip existing files (objects with filePath)
                    if (file instanceof File) {
                        formData.append("audio_files", file);
                    }
                    // If it's a string path, append it
                    else if (typeof file === "string") {
                        formData.append("audio_files", file);
                    }
                    // Skip objects with filePath (existing files on server)
                });
            } else if (data.audio_files instanceof File) {
                formData.append("audio_files", data.audio_files);
            } else if (typeof data.audio_files === "string") {
                formData.append("audio_files", data.audio_files);
            }
        }

        // Append icon (single file)
        if (data.icon) {
            if (data.icon instanceof File) {
                formData.append("icon", data.icon);
            } else if (typeof data.icon === "string") {
                formData.append("icon", data.icon);
            }
        }

        if (mode === "add") {
            // Debug FormData contents
            console.log("FormData contents:");
            for (let [key, value] of formData.entries()) {
                if (value instanceof File) {
                    console.log(key, `File: ${value.name} (${value.size} bytes)`);
                } else {
                    console.log(key, value);
                }
            }
            axiosInstance
                .post(ApiEndPoints.CULTURAL.create, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((response) => {
                    toastSuccess(response.data?.message || "Cultural Module added successfully");
                    navigate("/cultural-module");
                })
                .catch((error) => {
                    toastError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            // Edit mode - fetch current data to get is_active
            axiosInstance
                .get(ApiEndPoints.CULTURAL.listById(id))
                .then((response) => {
                    const currentData = response.data?.data?.module;
                    formData.append("is_active", currentData?.is_active || true);

                    return axiosInstance.patch(ApiEndPoints.CULTURAL.update(id), formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    });
                })
                .then((response) => {
                    toastSuccess(response.data?.message || "Cultural Module updated successfully");
                    navigate("/cultural-module");
                })
                .catch((error) => {
                    toastError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    if (fetching) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <PageHeader
                title={
                    <Typography variant="h5">
                        {mode === "add" ? "Add Cultural Module" : "Edit Cultural Module"}
                    </Typography>
                }
                action={
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/cultural-module")}
                        sx={{
                            borderColor: "#d0d7de",
                            color: "#656d76",
                            "&:hover": {
                                borderColor: "#7338AC",
                                color: "#7338AC",
                            },
                        }}
                    >
                        Back
                    </Button>
                }
            />

            <Card sx={{ mt: 3 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={8}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor='title'> Title</FormLabel>
                                    <Controller
                                        name="title"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                error={!!errors.title}
                                                helperText={errors.title?.message}
                                                sx={textFieldStyles}
                                                inputProps={{ maxLength: 100 }}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor='short_description'> Short Description</FormLabel>
                                    <Controller
                                        name="short_description"
                                        control={control}
                                        render={({ field }) => {
                                            const currentLength = field.value?.length || 0;
                                            const maxLength = 100;
                                            const remainingChars = maxLength - currentLength;

                                            return (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    error={!!errors.short_description}
                                                    helperText={
                                                        errors.short_description?.message ||
                                                        `${currentLength}/${maxLength} characters`
                                                    }
                                                    sx={textFieldStyles}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        if (newValue.length <= maxLength) {
                                                            field.onChange(e);
                                                        }
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor='description'>Description</FormLabel>
                                    <Controller
                                        name='description'
                                        control={control}
                                        render={({ field }) => (
                                            <ReactQuill
                                                theme='snow'
                                                value={field.value}
                                                modules={{
                                                    toolbar: [
                                                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                                        ['bold', 'italic', 'underline', 'strike'],
                                                        ['blockquote', 'code-block'],
                                                        [{ list: 'ordered' }, { list: 'bullet' }],
                                                        [{ script: 'sub' }, { script: 'super' }],
                                                        [{ indent: '-1' }, { indent: '+1' }],
                                                        [{ direction: 'rtl' }],
                                                        [{ size: ['small', false, 'large', 'huge'] }],
                                                        [{ color: [] }, { background: [] }],
                                                        [{ font: [] }],
                                                        ['link'],
                                                        ['clean']
                                                    ]
                                                }}
                                                formats={[
                                                    'header',
                                                    'font',
                                                    'size',
                                                    'bold',
                                                    'italic',
                                                    'underline',
                                                    'strike',
                                                    'blockquote',
                                                    'list',
                                                    'bullet',
                                                    'indent',
                                                    'script',
                                                    'link',
                                                    'color',
                                                    'background'
                                                ]}
                                                onChange={value => {
                                                    setValue('description', value, { shouldValidate: true })
                                                    field.onChange(value)
                                                }}
                                                sx={{
                                                    "& .quill": {
                                                        borderRadius: '8px',
                                                    },
                                                }}
                                                inputProps={{ maxLength: 400 }}
                                            />
                                        )}
                                    />
                                    {errors.description && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.description.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="region"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth error={!!errors.region}>
                                            <InputLabel>Region</InputLabel>
                                            <Select
                                                {...field}
                                                label="Region"
                                                sx={textFieldStyles}
                                            >
                                                {regions.map((region) => (
                                                    <MenuItem key={region.id} value={region.id}>
                                                        {region.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.region && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.region.message}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <Controller
                                    name="ai_setting.Genre"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Genre"
                                            error={!!errors.ai_setting?.Genre}
                                            helperText={errors.ai_setting?.Genre?.message}
                                            sx={textFieldStyles}
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <Controller
                                    name="ai_setting.Scale"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Scale"
                                            error={!!errors.ai_setting?.Scale}
                                            helperText={errors.ai_setting?.Scale?.message}
                                            sx={textFieldStyles}
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <Controller
                                    name="ai_setting.Tempo"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Tempo"
                                            error={!!errors.ai_setting?.Tempo}
                                            helperText={errors.ai_setting?.Tempo?.message}
                                            sx={textFieldStyles}
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Icon Upload */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <FormLabel>Icon</FormLabel>
                                    <Controller
                                        name="icon"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <CustomFileUploads
                                                multiple={false}
                                                subtitle="(Max file size 3mb) - JPG, PNG, PDF only"
                                                minHeight="100px"
                                                files={value}
                                                onChange={onChange}
                                                title={"Add Icon"}
                                                accept=".jpg, .jpeg, .png, .pdf"
                                                MediaUrl={MEDIA_URL}
                                            />
                                        )}
                                    />
                                    {errors?.icon && (
                                        <FormHelperText sx={{ color: "error.main" }}>
                                            {errors?.icon?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            {/* AI Prompt Section */}
                            <Grid item xs={12}>
                                <Grid container spacing={3}>
                                    <Grid item container spacing={3} xs={12}>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                AI Prompt
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="ai_prompt_title"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="AI Prompt Title"
                                                        error={!!errors.ai_prompt_title}
                                                        helperText={errors.ai_prompt_title?.message}
                                                        sx={textFieldStyles}
                                                        inputProps={{ maxLength: 100 }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="ai_prompt_description"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        label="AI Prompt Description"
                                                        error={!!errors.ai_prompt_description}
                                                        helperText={errors.ai_prompt_description?.message}
                                                        sx={textFieldStyles}
                                                        inputProps={{ maxLength: 100 }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Notation and Audio Section */}
                            <Grid item xs={12}>
                                <Grid container spacing={3}>
                                    {/* Notation Section */}
                                    <Grid item container spacing={3} xs={12} md={6}>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                Notation
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="notation_title"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="Notation Title"
                                                        error={!!errors.notation_title}
                                                        helperText={errors.notation_title?.message}
                                                        sx={textFieldStyles}
                                                        inputProps={{ maxLength: 100 }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="notation_description"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        label="Notation Description"
                                                        error={!!errors.notation_description}
                                                        helperText={errors.notation_description?.message}
                                                        sx={textFieldStyles}
                                                        inputProps={{ maxLength: 100 }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <FormLabel>Notation files</FormLabel>
                                                <Controller
                                                    name="notation_files"
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <CustomFileUploads
                                                            multiple={false}
                                                            subtitle="(Max file size 3mb) - JPG, PNG, PDF only"
                                                            minHeight="100px"
                                                            files={value}
                                                            onChange={onChange}
                                                            title={"Add Notation"}
                                                            accept=".jpg, .jpeg, .png, .pdf"
                                                            MediaUrl={MEDIA_URL}
                                                            sx={{p: 0}}
                                                        />
                                                    )}
                                                />
                                                {errors?.notation_files && (
                                                    <FormHelperText sx={{ color: "error.main" }}>
                                                        {errors?.notation_files?.message}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    {/* Audio Section */}
                                    <Grid item container spacing={3} xs={12} md={6}>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                Audio
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="audio_title"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="Audio Title"
                                                        error={!!errors.audio_title}
                                                        helperText={errors.audio_title?.message}
                                                        sx={textFieldStyles}
                                                        inputProps={{ maxLength: 100 }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Controller
                                                name="audio_description"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        label="Audio Description"
                                                        error={!!errors.audio_description}
                                                        helperText={errors.audio_description?.message}
                                                        sx={textFieldStyles}
                                                        inputProps={{ maxLength: 100 }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <FormLabel>Audio files</FormLabel>
                                                <Controller
                                                    name="audio_files"
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <CustomFileUploads
                                                            multiple={true}
                                                            subtitle="(Max file size 3mb) - MP3 only"
                                                            minHeight="100px"
                                                            files={value}
                                                            onChange={onChange}
                                                            title={"Add Audio"}
                                                            accept=".mp3"
                                                            MediaUrl={MEDIA_URL}
                                                            sx={{p: 0}}
                                                        />
                                                    )}
                                                />
                                                {errors?.audio_files && (
                                                    <FormHelperText sx={{ color: "error.main" }}>
                                                        {errors?.audio_files?.message}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                                    <Button
                                        onClick={() => navigate("/cultural-module")}
                                        variant="outlined"
                                        sx={{
                                            borderColor: "#d0d7de",
                                            color: "#656d76",
                                            "&:hover": {
                                                borderColor: "#7338AC",
                                                color: "#7338AC",
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        type="submit"
                                        loading={loading}
                                        variant="contained"
                                    >
                                        {mode === "add" ? "Add Cultural Module" : "Update Cultural Module"}
                                    </LoadingButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AddEditCulturalModule;

